import { createContext, use, useState, type ReactNode } from 'react';

/**
 * REACT-SCOPE — see the banner in src/styles/globals.css.
 *
 * The theme's document-level painting rules (the `@layer base` reset, the element,
 * `.text-*` and `.heading-*` overrides) are confined to this attribute. The SPA carries
 * it on <body> (set statically in index.html); each embedded widget bundle sets it on its
 * own mount container — never on the legacy host's <body> or <html> — so the widget
 * carries its own theme and the surrounding legacy page is left untouched.
 */
export const REACT_SCOPE_ATTRIBUTE = 'data-shiplex-react-root';

/**
 * Portalled UI — Radix dialogs/menus/popovers/selects/sheets/tooltips and the VOY float
 * components — mounts OUTSIDE the React tree, into `document.body` by default.
 *
 * In the SPA `document.body` is the React scope root itself, so the theme reaches those
 * surfaces. In an embedded widget bundle the scope root is the widget's mount container
 * and `document.body` belongs to the legacy host, so a portal left on its default escapes
 * the theme: it loses the `@layer base` reset (notably `border-color`, which Tailwind v4
 * otherwise leaves at `currentColor`), the font smoothing and the `.text-*` overrides.
 *
 * `ReactScopePortalRoot` renders a scoped portal host INSIDE the widget's root and
 * publishes it here. Portal call sites read it via `useReactScopePortalContainer` (the
 * Radix `container` prop) or `useReactScopePortalTarget` (raw `createPortal`). Outside a
 * widget the context is empty and every portal keeps its `document.body` default, so the
 * SPA is unchanged — and the legacy host page still never carries the scope attribute.
 *
 * The host is a full-viewport `position: fixed` layer, NOT a zero-box element. Moving a
 * portal off `document.body` also moves its clipping and stacking ancestry into the
 * legacy host's DOM, which we do not own: an `overflow: hidden|auto` ancestor would clip
 * the VOY float components (they use floating-ui's default `absolute` strategy), and an
 * in-flow host would leave overlays subject to whatever the surrounding markup does.
 * `fixed inset-0` escapes ancestor overflow clipping; `pointer-events: none` on the layer
 * with `pointer-events: auto` on its children keeps the widget clickable while no overlay
 * is open. The layer deliberately does NOT carry the scope attribute — it is already a
 * descendant of the mount container that does, so `:where([data-shiplex-react-root]) *`
 * reaches its subtree, and repeating the attribute here would apply the scope root's own
 * `bg-background` to a full-viewport box.
 */
const ReactScopePortalContext = createContext<HTMLElement | null>(null);

/** Radix `container` prop — `undefined` (the Radix default, `document.body`) outside a widget. */
export const useReactScopePortalContainer = (): HTMLElement | undefined => use(ReactScopePortalContext) ?? undefined;

/** `createPortal` target — `document.body` outside a widget. */
export const useReactScopePortalTarget = (): HTMLElement => use(ReactScopePortalContext) ?? document.body;

interface ReactScopePortalRootProps {
  children: ReactNode;
}

export const ReactScopePortalRoot = ({ children }: ReactScopePortalRootProps) => {
  const [portalHost, setPortalHost] = useState<HTMLDivElement | null>(null);

  return (
    <ReactScopePortalContext value={portalHost}>
      {children}
      <div ref={setPortalHost} className="pointer-events-none fixed inset-0 [&>*]:pointer-events-auto" />
    </ReactScopePortalContext>
  );
};
